import { useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import {
  PayoutsFeeEstimateParams,
  usePayoutsFeeEstimateQuery,
} from 'hooks/queries/usePayoutsFeeEstimateQuery'
import { BeneficiaryDetails } from 'types/beneficiaries'
import { selectedWalletAtom } from '../atoms/atoms'
import { getInitialMaxPayoutAmountRequest } from '../utils/payFormUtils'

interface UsePaymentFeeRequestProps {
  beneficiaryReference?: string
  paymentReference?: string
  selectedBeneficiary?: BeneficiaryDetails
}

const usePaymentFeeRequest = ({
  beneficiaryReference,
  paymentReference,
  selectedBeneficiary,
}: UsePaymentFeeRequestProps) => {
  const [maxPayoutAmount, setPayoutMaxAmount] = useState(0)
  const sourceWallet = useAtomValue(selectedWalletAtom)

  const isPayoutsV2Enabled = !!sourceWallet?.supportedTransferDestinations?.length

  const initialPayoutFeeRequestParams: PayoutsFeeEstimateParams = useMemo(() => {
    const isValid =
      !!beneficiaryReference &&
      !!paymentReference &&
      !!sourceWallet &&
      !!selectedBeneficiary &&
      !sourceWallet?.supportsInternalBvnkNetworkTransfers

    if (isValid) {
      return {
        request: getInitialMaxPayoutAmountRequest({
          beneficiaryReference,
          paymentReference,
          sourcWallet,
          isPayoutsV2Enabled,
        }),
        isEnabled: false,
      }
    }
  }, [
    beneficiaryReference,
    paymentReference,
    sourcWallet?.walletId,
    isPayoutsV2Enabled,
    selectedBeneficiary?.transferDestination,
  ])

  const {
    data: initialFeeForMaxAmount,
    refetch: fetchInitialFeeForMaxAmount,
    isFetching: isFetchingMaxPayoutAmount,
    error: fetchingMaxPayoutAmountError,
  } = usePayoutsFeeEstimateQuery(initialPayoutFeeRequestParams)

  useMemo(() => {
    !!initialPayoutFeeRequestParams && fetchInitialFeeForMaxAmount()
  }, [initialPayoutFeeRequestParams])

  useMemo(() => {
    if (!!initialFeeForMaxAmount) {
      setPayoutMaxAmount(initialFeeForMaxAmount?.availablePayoutAmount?.value)
    }
  }, [initialFeeForMaxAmount])

  return {
    maxPayoutAmount,
    isFetchingMaxPayoutAmount,
    fetchingMaxPayoutAmountError,
  }
}

export default usePaymentFeeRequest
